/* ImageUploadField — reusable upload field (reuse engine POST /api/upload).
   Client-side validation: ext png/jpg/jpeg/webp, max 2 MB (sinkron limit backend). */
import React, { useRef, useState } from "react";
import { Upload, Trash2, RefreshCw, Loader2, ImageIcon } from "lucide-react";
import api, { imageUrl, formatApiError } from "@/lib/api";
import { pickUploadValue } from "@/lib/uploadValue";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const ALLOWED_EXT = ["png", "jpg", "jpeg", "webp"];
const MAX_SIZE = 2 * 1024 * 1024; // sinkron dengan MAX_UPLOAD_SIZE backend (2 MB)

const formatBytes = (b) =>
  b >= 1024 * 1024 ? `${(b / 1048576).toFixed(2)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

export default function ImageUploadField({
  label,
  required = false,
  value,
  onChange,
  notes = [],
  testId = "image-upload-field",
  onUploadingChange,
  previewHeight = "h-28",
  // "path" = storage key (default, Categories/Banners) | "url" = public /uploads/… URL
  // (dipakai field branding yang nanti di-absolutize SSR untuk og:image).
  valueField = "path",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [fileMeta, setFileMeta] = useState(null);

  const setBusy = (v) => { setUploading(v); onUploadingChange?.(v); };

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) { setError("Format gambar tidak didukung."); return; }
    if (file.size > MAX_SIZE) { setError("File terlalu besar. Maksimal 2 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const v = pickUploadValue(r.data, valueField);
      setFileMeta({ name: file.name, size: file.size });
      onChange(v);
    } catch (e) {
      setError(formatApiError(e) || "Upload gagal. Silakan coba kembali.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => { setFileMeta(null); setError(""); onChange(""); };

  return (
    <div className="space-y-1.5" data-testid={testId}>
      {label && (
        <Label className="font-bold">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />

      {value ? (
        <div className="rounded-xl border border-[#E5E5E5] overflow-hidden">
          <div className={`${previewHeight} overflow-hidden bg-[#FAF9F6] grid place-items-center p-2`}>
            <img
              src={imageUrl(value)}
              alt={label || "Preview"}
              className="h-full w-full object-contain"
              data-testid={`${testId}-preview`}
            />
          </div>
          <div className="flex items-center gap-2 px-3 py-2 border-t border-[#E5E5E5] bg-white">
            <span className="flex-1 min-w-0 text-xs text-[#5C5C5C] truncate" data-testid={`${testId}-filemeta`}>
              {fileMeta ? `${fileMeta.name} · ${formatBytes(fileMeta.size)}` : value.split("/").pop()}
            </span>
            <Button type="button" size="sm" variant="outline" className="rounded-lg h-8 text-xs"
              disabled={uploading} onClick={() => inputRef.current?.click()} data-testid={`${testId}-replace`}>
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Ganti
            </Button>
            <Button type="button" size="sm" variant="outline" className="rounded-lg h-8 text-xs text-[#DC2F02]"
              disabled={uploading} onClick={clear} data-testid={`${testId}-remove`}>
              <Trash2 size={12} /> Hapus
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full rounded-xl border-2 border-dashed border-[#E5E5E5] hover:border-[color:var(--brand-primary,#E85D04)] transition-colors bg-[#FAF9F6] p-5 flex flex-col items-center gap-2 text-[#5C5C5C]"
          data-testid={`${testId}-pick`}
        >
          {uploading ? (
            <><Loader2 size={22} className="animate-spin" /><span className="text-xs font-bold">Mengunggah…</span></>
          ) : (
            <>
              <span className="w-10 h-10 rounded-full bg-white border border-[#E5E5E5] grid place-items-center">
                {value === "" && <ImageIcon size={18} className="opacity-50" />}
              </span>
              <span className="text-xs font-bold flex items-center gap-1"><Upload size={12} /> Pilih gambar dari perangkat</span>
            </>
          )}
        </button>
      )}

      {error && (
        <p className="text-xs text-red-600 font-semibold" data-testid={`${testId}-error`}>{error}</p>
      )}
      {notes.length > 0 && (
        <ul className="text-[11px] text-[#5C5C5C] leading-relaxed space-y-0.5" data-testid={`${testId}-notes`}>
          {notes.map((n, i) => <li key={i}>• {n}</li>)}
        </ul>
      )}
    </div>
  );
}
